import React, { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useSearch, useLocation } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  BookOpen,
  MessageCircle,
  Ticket,
  User,
  LogOut,
  ChevronDown,
  Loader2,
  Menu,
  X,
  Palette,
  Sun,
  Moon,
  Monitor,
  Check,
} from "lucide-react";
import type { OrganizationInfo } from "../../lib/portal-loaders";
import { usePortalBranding } from "../../hooks/usePortalBranding";
import { useTheme } from "../../hooks/useTheme";

interface UnifiedCustomerPortalLayoutProps {
  children: React.ReactNode;
  organizationInfo: OrganizationInfo;
}

interface NavigationItem {
  title: string;
  icon: React.ComponentType<Record<string, unknown>>;
  href: string;
  showWhenSignedOut?: boolean;
  showWhenSignedIn?: boolean;
}

export const UnifiedCustomerPortalLayout: React.FC<
  UnifiedCustomerPortalLayoutProps
> = ({ children, organizationInfo }) => {
  const {
    user,
    customer,
    logout,
    isAuthenticated,
    isCustomerPortal,
    isLoading,
  } = useAuth();
  const navigate = useNavigate();
  const currentSearchParams = useSearch({ strict: false });
  const location = useLocation();
  const {
    getThemedPrimaryColor,
    getThemedSecondaryColor,
    getThemedPrimaryWithOpacity,
    getThemedSecondaryWithOpacity,
    getContrastText,
  } = usePortalBranding();
  const { theme, setTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Show loading spinner while auth is being initialized
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2
            className="h-8 w-8 animate-spin mx-auto mb-4"
            style={{ color: getThemedPrimaryColor() }}
          />
          <p className="text-gray-600 dark:text-gray-400">Loading portal...</p>
        </div>
      </div>
    );
  }

  // Determine URL pattern (path-based vs subdomain-based)
  const isPathBased = window.location.pathname.startsWith("/org/");
  const getPortalUrl = (path: string) => {
    if (isPathBased) {
      return `/org/${organizationInfo.subdomain}/portal${path}`;
    }
    return `/portal${path}`;
  };

  // Navigation items - show different items based on auth status
  const navigationItems: NavigationItem[] = [
    {
      title: "Knowledge Base",
      icon: BookOpen,
      href: getPortalUrl("/knowledge-base"),
      showWhenSignedOut: true,
      showWhenSignedIn: true,
    },
    {
      title: "Submit Ticket",
      icon: MessageCircle,
      href: getPortalUrl("/submit-ticket"),
      showWhenSignedOut: false,
      showWhenSignedIn: true,
    },
    {
      title: "My Tickets",
      icon: Ticket,
      href: getPortalUrl("/tickets"),
      showWhenSignedOut: false,
      showWhenSignedIn: true,
    },
  ];

  const handleNavigation = (href: string) => {
    setMobileMenuOpen(false); // Close mobile menu on navigation

    // Preserve search params when navigating to tickets page
    if (href.endsWith("/tickets")) {
      navigate({
        to: href,
        search: currentSearchParams as Record<string, unknown> // Preserve existing search params
      });
    } else {
      navigate({ to: href });
    }
  };

  const handleLogin = () => {
    navigate({ to: getPortalUrl("/login") });
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Always redirect to knowledge base after logout attempt
      const homeUrl = getPortalUrl("/knowledge-base");
      navigate({ to: homeUrl });
    }
  };

  const currentPath = location.pathname;
  const isSignedIn = isAuthenticated && isCustomerPortal;
  const customerName = customer
    ? `${customer.firstName} ${customer.lastName}`
    : user?.firstName
      ? `${user.firstName} ${user.lastName}`
      : "Customer";
  const customerEmail = customer?.email || user?.email || "";
  const customerInitials =
    customerName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "C";

  // Check if we're on login or verify pages (should not show navigation)
  const isAuthPage =
    currentPath.includes("/login") || currentPath.includes("/verify");

  // Filter navigation items based on auth status
  const visibleNavItems = navigationItems.filter((item) =>
    isSignedIn ? item.showWhenSignedIn : item.showWhenSignedOut
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header - only show on non-auth pages */}
      {!isAuthPage && (
        <header
          className="sticky top-0 z-50 bg-white dark:bg-gray-800"
          style={{
            borderTop: `4px solid ${getThemedPrimaryColor()}`,
            borderBottom: `3px solid ${getThemedSecondaryColor()}`,
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              {/* Left side: Hamburger menu (mobile) + Logo/Org name */}
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Hamburger menu button - only show on mobile */}
                {visibleNavItems.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="md:hidden p-2"
                    aria-label="Toggle menu"
                  >
                    {mobileMenuOpen ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                  </Button>
                )}

                {/* Organization name and logo - clickable to go home */}
                <button
                  onClick={() => handleNavigation(getPortalUrl(""))}
                  className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity text-left"
                >
                  {/* Logo */}
                  {organizationInfo.portalSettings.branding.logoUrl ? (
                    <div
                      className="p-2 rounded-lg dark:bg-white/35 transition-all duration-200"
                      style={{
                        border: `1px solid transparent`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor =
                          getThemedSecondaryWithOpacity(0.3);
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "transparent";
                      }}
                    >
                      <img
                        src={organizationInfo.portalSettings.branding.logoUrl}
                        alt={`${organizationInfo.name} logo`}
                        className="h-8 sm:h-10 w-auto object-contain"
                        onError={(e) => {
                          // Hide logo if it fails to load
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  ) : (
                    <div>
                      <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                        {organizationInfo.name}
                      </h1>
                      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        Support Portal
                      </p>
                    </div>
                  )}
                </button>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center flex-1 ml-8">
                <div className="flex items-center space-x-6">
                  {visibleNavItems.map((item) => {
                    const isActive =
                      currentPath === item.href ||
                      currentPath.startsWith(`${item.href}/`);
                    return (
                      <button
                        key={item.href}
                        onClick={() => handleNavigation(item.href)}
                        className="flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-all duration-200 border-b-3 relative"
                        style={
                          isActive
                            ? {
                                color: getThemedPrimaryColor(),
                                backgroundColor:
                                  getThemedPrimaryWithOpacity(0.15),
                                fontWeight: 600,
                                borderBottom: `3px solid ${getThemedPrimaryColor()}`,
                              }
                            : {
                                color: getThemedSecondaryColor(),
                                borderBottom: "3px solid transparent",
                              }
                        }
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor =
                              getThemedPrimaryWithOpacity(0.08);
                            e.currentTarget.style.color =
                              getThemedPrimaryColor();
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.color =
                              getThemedSecondaryColor();
                          }
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.outline = `2px solid ${getThemedSecondaryColor()}`;
                          e.currentTarget.style.outlineOffset = "2px";
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.outline = "none";
                        }}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* User section */}
              <div className="flex items-center space-x-2 sm:space-x-4">
                {isSignedIn ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 transition-all duration-200"
                        style={{
                          borderColor: "transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor =
                            getThemedPrimaryWithOpacity(0.1);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        <div
                          className="h-8 w-8 sm:h-9 sm:w-9 text-xs sm:text-sm rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ring-2 ring-transparent"
                          style={{
                            backgroundColor: getThemedPrimaryColor(),
                            color: getContrastText(getThemedPrimaryColor()),
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = `0 0 0 2px ${getThemedSecondaryColor()}`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = "0 0 0 2px transparent";
                          }}
                        >
                          {customerInitials}
                        </div>
                        <div className="hidden sm:block text-left">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {customerName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]">
                            {customerEmail}
                          </div>
                        </div>
                        <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {customerName}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {customerEmail}
                        </p>
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() =>
                          handleNavigation(getPortalUrl("/profile"))
                        }
                        className="cursor-pointer"
                      >
                        <User className="mr-2 h-4 w-4" />
                        Profile Settings
                      </DropdownMenuItem>
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <Palette className="mr-2 h-4 w-4" />
                          Appearance
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem
                            onClick={() => setTheme("light")}
                            className="cursor-pointer"
                          >
                            <Sun className="mr-2 h-4 w-4" />
                            Light
                            {theme === "light" && (
                              <Check className="ml-auto h-4 w-4" />
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setTheme("dark")}
                            className="cursor-pointer"
                          >
                            <Moon className="mr-2 h-4 w-4" />
                            Dark
                            {theme === "dark" && (
                              <Check className="ml-auto h-4 w-4" />
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setTheme("system")}
                            className="cursor-pointer"
                          >
                            <Monitor className="mr-2 h-4 w-4" />
                            System
                            {theme === "system" && (
                              <Check className="ml-auto h-4 w-4" />
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer"
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogin}
                    className="transition-all duration-200"
                    style={{
                      color: getThemedPrimaryColor(),
                      borderColor: getThemedPrimaryColor(),
                      borderWidth: "2px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor =
                        getThemedPrimaryColor();
                      e.currentTarget.style.color = getContrastText(
                        getThemedPrimaryColor()
                      );
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = getThemedPrimaryColor();
                    }}
                  >
                    Sign In
                  </Button>
                )}
              </div>
            </div>

            {/* Mobile navigation slide-down menu */}
            {mobileMenuOpen && visibleNavItems.length > 0 && (
              <div
                className="md:hidden py-2"
                style={{
                  borderTop: `3px solid ${getThemedPrimaryColor()}`,
                  backgroundColor: getThemedPrimaryWithOpacity(0.03),
                }}
              >
                <nav className="flex flex-col space-y-1">
                  {visibleNavItems.map((item) => {
                    const isActive =
                      currentPath === item.href ||
                      currentPath.startsWith(`${item.href}/`);
                    return (
                      <button
                        key={item.href}
                        onClick={() => handleNavigation(item.href)}
                        className="flex items-center justify-between px-4 py-3 text-base font-medium transition-all duration-200 text-left w-full"
                        style={
                          isActive
                            ? {
                                color: getThemedPrimaryColor(),
                                backgroundColor:
                                  getThemedPrimaryWithOpacity(0.12),
                                borderLeft: `4px solid ${getThemedPrimaryColor()}`,
                                fontWeight: 600,
                              }
                            : {
                                color: getThemedSecondaryColor(),
                                borderLeft: "4px solid transparent",
                              }
                        }
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor =
                              getThemedPrimaryWithOpacity(0.08);
                            e.currentTarget.style.color =
                              getThemedPrimaryColor();
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                            e.currentTarget.style.color =
                              getThemedSecondaryColor();
                          }
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <item.icon className="h-5 w-5" />
                          <span>{item.title}</span>
                        </div>
                        {isActive && (
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: getThemedSecondaryColor() }}
                          />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Main content */}
      <main
        className={
          isAuthPage
            ? "min-h-screen flex items-center justify-center px-4"
            : "flex-1 py-4 sm:py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
        }
      >
        {children}
      </main>
    </div>
  );
};
